import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconMessage, IconMessages } from '@tabler/icons-react';
import {
  Avatar,
  Button,
  EmptyState,
  Loader,
  Pressable,
  ProximityScope,
  ScrollArea,
  NavIconFlyout,
  tokens,
  useMediaQuery,
  WP_ICON_NAV,
  WP_ICON_NAV_MOBILE,
} from '@wapharma/design-system';
import { getToken } from '../auth';
import { useToast } from '../contexts/ToastContext';
import { useConversation } from '../hooks/useConversation';
import { useConversationList } from '../hooks/useConversationList';
import { conversationInboxPreview, conversationInboxTime, conversationTitle } from '../lib/conversationDisplay';
import { getEntityLabel } from '../lib/entityRegistry';
import {
  onOpenMessagesFlyout,
  resolveFlyoutDraftReferences,
  type MessagesFlyoutDraftReference,
  type MessagesFlyoutEntityTarget,
} from '../lib/messagesFlyout';
import { EntityTypeMark } from './EntityTypeMark';
import { MessagesConversationPane } from './MessagesConversationPane';
import styles from './MessagesNavFlyout.module.css';

const MESSAGES_INBOX_EXCLUDE_ENTITY_TYPES = ['SupportTicket'] as const;

function entityThreadLabels(entity: MessagesFlyoutEntityTarget): {
  title: string;
  subtitle: string | null;
} {
  const entityLabel = getEntityLabel(entity.entityType, entity.entityId);
  if (entity.audience === 'INTERNAL') {
    return { title: 'Équipe interne', subtitle: entityLabel };
  }
  return { title: entityLabel, subtitle: 'Client' };
}

export function MessagesNavFlyout() {
  const narrow = useMediaQuery(tokens.media.mdMax);
  const iconProps = narrow ? WP_ICON_NAV_MOBILE : WP_ICON_NAV;
  const token = getToken();
  const { showToast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingEntity, setPendingEntity] = useState<MessagesFlyoutEntityTarget | null>(null);
  const [pendingDraftReferences, setPendingDraftReferences] = useState<MessagesFlyoutDraftReference[]>([]);
  const lastListErrorRef = useRef<string | null>(null);
  const lastThreadErrorRef = useRef<string | null>(null);

  const list = useConversationList(token, {
    page: 1,
    limit: 12,
    status: 'OPEN',
    excludeEntityTypes: [...MESSAGES_INBOX_EXCLUDE_ENTITY_TYPES],
  });

  const selectedSummary = useMemo(
    () => list.items.find((item) => item.id === selectedId) ?? null,
    [list.items, selectedId],
  );
  const handleMarkedRead = useCallback(() => {
    void list.refresh();
  }, [list.refresh]);

  const thread = useConversation({
    mode: 'id',
    conversationId: selectedId,
    token,
    enabled: Boolean(selectedId),
    onMarkedRead: handleMarkedRead,
  });
  const pendingThread = useConversation({
    entityType: pendingEntity?.entityType ?? 'Order',
    entityId: pendingEntity?.entityId ?? null,
    audience: pendingEntity?.audience ?? 'INTERNAL',
    token,
    enabled: Boolean(pendingEntity?.entityType && pendingEntity?.entityId),
  });

  const detailOpen = selectedId != null || pendingEntity != null;
  const unread = list.unreadTotal > 0 ? list.unreadTotal : undefined;

  useEffect(
    () =>
      onOpenMessagesFlyout((request) => {
        if (request.conversationId) {
          setPendingEntity(null);
          setPendingDraftReferences([]);
          setSelectedId(request.conversationId);
          return;
        }
        setSelectedId(null);
        setPendingEntity(request.openEntity ?? null);
        setPendingDraftReferences(resolveFlyoutDraftReferences(request));
      }),
    [],
  );

  useEffect(() => {
    if (!list.error) {
      lastListErrorRef.current = null;
      return;
    }
    if (lastListErrorRef.current === list.error) return;
    lastListErrorRef.current = list.error;
    showToast(list.error, { tone: 'error' });
  }, [list.error, showToast]);

  useEffect(() => {
    if (!thread.error) {
      lastThreadErrorRef.current = null;
      return;
    }
    if (lastThreadErrorRef.current === thread.error) return;
    lastThreadErrorRef.current = thread.error;
    showToast(thread.error, { tone: 'error' });
  }, [thread.error, showToast]);

  useEffect(() => {
    if (!pendingEntity || pendingThread.loading) return;
    const conv = pendingThread.conversation;
    if (!conv?.id) return;
    /*
     * Promote only pre-existing threads (resolve found mail).
     * Brand-new empty shell from ensureConversation must stay pending until
     * sendMessage finishes — otherwise id-mode GET races the POST and the feed
     * sticks empty until full page refresh.
     */
    const hasMail = (conv.messages?.length ?? 0) > 0 || Boolean(conv.lastMessageAt);
    if (!hasMail) return;
    setSelectedId(conv.id);
    setPendingEntity(null);
  }, [pendingEntity, pendingThread.loading, pendingThread.conversation]);

  const activeConversation = selectedId
    ? (thread.conversation ??
      (pendingThread.conversation?.id === selectedId ? pendingThread.conversation : null))
    : pendingThread.conversation;
  const activeLoading = selectedId
    ? thread.loading && !thread.conversation && !pendingThread.conversation
    : pendingThread.loading;
  const pendingLabels = pendingEntity ? entityThreadLabels(pendingEntity) : null;
  const activeTitle = selectedId
    ? selectedSummary
      ? conversationTitle(selectedSummary)
      : thread.conversation
        ? conversationTitle(thread.conversation)
        : pendingThread.conversation
          ? conversationTitle(pendingThread.conversation)
          : 'Conversation'
    : pendingThread.conversation
      ? conversationTitle(pendingThread.conversation)
      : pendingLabels?.title ?? 'Conversation';
  const activeSubtitle = selectedId
    ? selectedSummary?.displaySubtitle ??
      thread.conversation?.displaySubtitle ??
      pendingThread.conversation?.displaySubtitle ??
      null
    : pendingThread.conversation?.displaySubtitle ?? pendingLabels?.subtitle ?? null;
  const activeAvatarUrl = selectedId
    ? selectedSummary?.displayAvatarUrl ??
      thread.conversation?.displayAvatarUrl ??
      pendingThread.conversation?.displayAvatarUrl ??
      null
    : pendingThread.conversation?.displayAvatarUrl ?? null;
  const activeThread = selectedId ? thread : pendingThread;
  const awaitingStart = Boolean(pendingEntity && !pendingThread.conversation?.id);

  const handleSend = useCallback(
    async (input: Parameters<typeof activeThread.sendMessage>[0]) => {
      const conversationId = await activeThread.sendMessage(input);
      if (!conversationId) return;
      /* After first send from entity open: pin id-mode; keep pending cache until GET lands. */
      if (pendingEntity) {
        setSelectedId(conversationId);
        setPendingEntity(null);
        setPendingDraftReferences([]);
      }
      void list.refresh();
    },
    [activeThread, list, pendingEntity],
  );

  return (
    <NavIconFlyout
      icon={<IconMessage {...iconProps} aria-hidden />}
      label="Messages"
      unseenCount={unread}
      triggerVariant="ghost"
      panelVariant={detailOpen ? 'expanded' : 'default'}
      panelPadding="flush"
      keepOpen={detailOpen}
      onRequestClose={() => {
        setSelectedId(null);
        setPendingEntity(null);
        setPendingDraftReferences([]);
      }}
    >
      <div className={`${styles.panel} ${detailOpen ? styles.panelDetail : ''}`}>
        {detailOpen ? (
          <MessagesConversationPane
            compact
            conversation={activeConversation}
            loading={activeLoading}
            title={activeTitle}
            subtitle={activeSubtitle}
            entityType={activeConversation?.entityType ?? pendingEntity?.entityType}
            avatarUrl={activeAvatarUrl}
            awaitingStart={awaitingStart}
            initialDraftReferences={pendingDraftReferences.length ? pendingDraftReferences : undefined}
            onBack={() => {
              setSelectedId(null);
              setPendingEntity(null);
              setPendingDraftReferences([]);
            }}
            onSend={handleSend}
            onAddReference={activeThread.addReference}
            onRemoveReference={activeThread.removeReference}
          />
        ) : (
          <div className={styles.inboxShell}>
            <div className={styles.header}>
              <span>Messages</span>
              {list.loading ? <Loader size="sm" /> : null}
            </div>
            <ScrollArea className={styles.listScrollHost} viewportClassName={styles.listScroll} maxHeight="none">
              <ProximityScope axis="y" className={styles.list}>
              {list.items.length ? (
                list.items.map((row) => {
                  const timeLabel = conversationInboxTime(row.lastMessageAt);
                  return (
                  <Pressable
                    key={row.id}
                    type="button"
                    data-wp-proximity-item=""
                    className={`${styles.row} ${row.unread ? styles.rowUnread : styles.rowRead}`}
                    onClick={() => setSelectedId(row.id)}
                  >
                    <div className={styles.avatarWrap}>
                      <Avatar
                        className={styles.avatar}
                        size="sm"
                        label={conversationTitle(row)}
                        src={row.displayAvatarUrl}
                      />
                    </div>
                    <div className={styles.body}>
                      <div className={styles.titleRow}>
                        <span className={styles.title}>{conversationTitle(row)}</span>
                        {timeLabel ? (
                          <span className={styles.previewTime}>{timeLabel}</span>
                        ) : null}
                      </div>
                      {row.entityType ? (
                        <EntityTypeMark entityType={row.entityType} className={styles.entityMark} />
                      ) : null}
                      <span className={styles.previewText}>{conversationInboxPreview(row)}</span>
                    </div>
                  </Pressable>
                  );
                })
              ) : (
                <div className={styles.emptyState}>
                  <EmptyState
                    density="compact"
                    title="Aucune conversation"
                    illustration={<IconMessages size={28} stroke={1.5} aria-hidden />}
                    action={
                      <Button type="button" variant="ghost" onClick={() => void list.refresh()}>
                        Actualiser
                      </Button>
                    }
                  />
                </div>
              )}
              </ProximityScope>
            </ScrollArea>
          </div>
        )}
      </div>
    </NavIconFlyout>
  );
}
